// :)
/**
 * TypeScript hook scripts for Bun runtime — written to ~/.synkro/hooks/ during `synkro install`.
 *
 * Each export is the full source code of a TypeScript file that runs via `#!/usr/bin/env bun`.
 * All grading, classification, CVE scanning, and persistence lives server-side.
 * Local mode: grade via local-cc channel; rules + embeddings from local PGLite only.
 */



// ─────────────────────────────────────────────────────────────────────────────
// STUB HOOKS (`synkro install --mode stub`)
//
// In stub mode every hook becomes ~3 lines that delegate to _synkro-stub-common.ts.
// The common file is pure PLUMBING — no scanning logic, no regexes, no verdict
// parsing. It reads the host-only inputs the container can't see (target file
// bytes, transcript tail, the repo's .synkro), POSTs a fixed envelope to the
// local server's /api/scan/{surface}, and prints the reply. All the brains live
// in the container (cli/scanning/*), so logic changes ship via a GHCR push with
// no CLI re-install. Authored with String.raw + string concatenation (no ${}, no
// inner backticks, no regex) so escapes survive materialization verbatim.
// ─────────────────────────────────────────────────────────────────────────────

export const STUB_COMMON_TS = String.raw`import { existsSync, readFileSync, mkdirSync, writeFileSync } from 'node:fs';
import { execSync } from 'node:child_process';
import { homedir } from 'node:os';
import { join } from 'node:path';

const HOME = homedir();
const PORT = process.env.SYNKRO_MCP_PORT || '18931';

function loadMcpJwt(): string {
  try { return readFileSync(join(HOME, '.synkro', '.mcp-jwt'), 'utf-8').trim(); } catch { return ''; }
}

let _cfgCache: Record<string, string> | null = null;
function cfgVal(key: string): string {
  if (process.env[key]) return process.env[key] as string;
  if (!_cfgCache) {
    _cfgCache = {};
    try {
      const txt = readFileSync(join(HOME, '.synkro', 'config.env'), 'utf-8');
      for (const line of txt.split('\n')) {
        const mm = line.match(/^([A-Z0-9_]+)=['"]?(.*?)['"]?\s*$/);
        if (mm) _cfgCache[mm[1]] = mm[2];
      }
    } catch {}
  }
  return _cfgCache[key] || '';
}
function deployIsCloud(): boolean { return cfgVal('SYNKRO_DEPLOY_LOCATION') === 'cloud'; }

// Cloud MCP gate: in cloud mode the local server (:18931) isn't running, so the
// gate asks the org policy directly (api /api/mcp/gate, authed by .mcp-jwt).
// Fail-open by contract — any error or non-block verdict allows the call.
async function cloudMcpGate(payload: any, harness: string): Promise<string> {
  try {
    const toolName = String(payload.tool_name || payload.tool || '');
    const parts = toolName.split('__');
    const server = (parts.length >= 3 && parts[0] === 'mcp') ? parts[1] : '';
    if (!server) return failOpen(harness);
    const base = (cfgVal('SYNKRO_GATEWAY_URL') || 'https://api.synkro.sh').replace(/\/+$/, '');
    const resp = await fetch(base + '/api/mcp/gate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + loadMcpJwt() },
      body: JSON.stringify({ server: server, tool: parts.slice(2).join('__') }),
      signal: AbortSignal.timeout(5000),
    });
    if (!resp.ok) return failOpen(harness);
    const d = await resp.json() as any;
    if (d && d.decision === 'block') {
      const reason = String(d.reason || ('MCP server "' + server + '" blocked by policy'));
      if (harness === 'cursor') return JSON.stringify({ permission: 'deny', user_message: reason });
      return JSON.stringify({ systemMessage: '[synkro:cloud:mcp] ' + reason, hookSpecificOutput: { hookEventName: 'PreToolUse', permissionDecision: 'deny', permissionDecisionReason: reason, additionalContext: reason } });
    }
    return failOpen(harness);
  } catch { return failOpen(harness); }
}

async function readStdin(): Promise<string> {
  const chunks: Buffer[] = [];
  for await (const chunk of process.stdin) chunks.push(Buffer.isBuffer(chunk) ? chunk : Buffer.from(chunk));
  return Buffer.concat(chunks).toString('utf-8');
}

function isCursor(harnessOpt?: string): boolean {
  return harnessOpt === 'cursor' || process.argv.includes('--cursor') || process.env.SYNKRO_HOOK_FORMAT === 'cursor';
}

function failOpen(harness: string): string {
  return harness === 'cursor' ? '{"permission":"allow"}' : '{}';
}

function out(s: string): void { try { process.stdout.write(s + '\n'); } catch {} }

function gitRoot(cwd: string): string {
  if (!cwd) return '';
  try {
    return execSync('git rev-parse --show-toplevel', { cwd, timeout: 2000, encoding: 'utf-8', stdio: ['pipe', 'pipe', 'pipe'] }).trim();
  } catch { return ''; }
}

// Once-per-session onboarding hint for repos with no synkro.toml file.
function noSynkroHint(sessionId: string): string | null {
  const dir = join(HOME, '.synkro', '.no-synkro-hint');
  try {
    mkdirSync(dir, { recursive: true });
    const key = (sessionId || 'nosession').replace(/[^a-zA-Z0-9_-]/g, '_');
    const marker = join(dir, key);
    if (existsSync(marker)) return null;
    writeFileSync(marker, '', { flag: 'w' });
  } catch {}
  return '[synkro] No synkro.toml config in this repo — grading skipped. Run synkro install here to enable Synkro.';
}

function filePathFromToolInput(ti: any): string {
  if (!ti || typeof ti !== 'object') return '';
  return ti.file_path || ti.notebook_path || ti.path || ti.target_file || '';
}

interface StubOpts {
  needsFile?: boolean;
  needsTranscript?: boolean;
  // Ship the WHOLE transcript (not just the 200KB tail). Sync surfaces need it
  // so the server can compute stable absolute message indices + total usage.
  fullTranscript?: boolean;
  needsPlan?: boolean;
  telemetry?: boolean;
  harness?: string;
}

export async function runStub(surface: string, opts: StubOpts = {}): Promise<void> {
  const harness = isCursor(opts.harness) ? 'cursor' : 'cc';
  try {
    const input = await readStdin();
    if (!input.trim()) { out(failOpen(harness)); return; }
    const payload = JSON.parse(input);

    // Cloud MCP enforcement: the gate can't reach the local server, so decide
    // against the org policy directly. Fail-open inside cloudMcpGate.
    if (surface === 'mcp-gate' && deployIsCloud()) { out(await cloudMcpGate(payload, harness)); return; }

    const workspaceRoots = Array.isArray(payload.workspace_roots) ? payload.workspace_roots : [];
    const cwd = (typeof payload.cwd === 'string' && payload.cwd) || workspaceRoots[0] || '';
    const sessionId = String(payload.session_id || payload.conversation_id || '');
    const root = gitRoot(cwd);

    // Dormancy: a repo is onboarded only if it has a synkro.toml FILE at its git
    // root. Guard root !== HOME — ~/.synkro is the config DIRECTORY, so without
    // this a home-rooted cwd (dotfiles in git, non-git dir under home) could
    // look onboarded.
    let synkroFileText = '';
    if (root && root !== HOME && existsSync(join(root, 'synkro.toml'))) {
      try { synkroFileText = readFileSync(join(root, 'synkro.toml'), 'utf-8'); } catch {}
    }
    if (!synkroFileText) {
      if (opts.telemetry) { out(failOpen(harness)); return; }
      const hint = noSynkroHint(sessionId);
      if (!hint) { out(failOpen(harness)); return; }
      if (harness === 'cursor') out(JSON.stringify({ permission: 'allow', user_message: hint }));
      else out(JSON.stringify({ systemMessage: hint, hookSpecificOutput: { hookEventName: 'PreToolUse', additionalContext: hint } }));
      return;
    }

    // Gather host-only inputs the container can't read.
    const envelope: any = { payload, harness, cwd: root || cwd, sessionId, synkroFileText };

    if (opts.needsFile) {
      const fp = filePathFromToolInput(payload.tool_input || {});
      if (fp && existsSync(fp)) {
        try { envelope.baseContent = readFileSync(fp, 'utf-8').slice(0, 65536); } catch {}
      }
    }
    if (opts.needsTranscript) {
      const tp = typeof payload.transcript_path === 'string' ? payload.transcript_path : '';
      if (tp && existsSync(tp)) {
        try {
          const t = readFileSync(tp, 'utf-8');
          envelope.transcript = (opts.fullTranscript || t.length <= 200000) ? t : t.slice(t.length - 200000);
        } catch {}
      }
    }
    if (opts.needsPlan) {
      const ti = payload.tool_input || {};
      envelope.planText = String(ti.plan || ti.content || payload.plan || '');
    }

    // bash-followup is telemetry, but it can carry the INLINE "task complete ✓ N/N"
    // result (the server waits up to 30s for the completion grade), so it needs a
    // wait above that budget — not the 6s telemetry default — or the stub abandons
    // the request before the result lands and the completion shows up a hook late.
    // prompt-submit is the opposite: it's INTERACTIVE (blocks the user's prompt from
    // being sent) under a short 5s hook timeout, so it gets ONE short attempt (below)
    // and a tight per-attempt budget that fits inside 5s — never the 6s×3 telemetry path.
    const timeoutMs = surface === 'bash-followup' ? 32000
      : surface === 'prompt-submit' ? 3500
      : (opts.telemetry ? 6000 : 28000);
    // Cloud has no local container to reach. Post the SAME envelope to the org's grader
    // via the gateway's /grade/submit as role 'scan:<surface>'; the container runs
    // scanRouter (runScan) server-side and returns the SAME shaped decision — so cloud
    // and local share ONE grading implementation, no client-side parsing. The gateway
    // accepts the durable 1yr MCP token (verifyOrg → org-derived, tenant-safe).
    const cloud = deployIsCloud();
    // Regex-free trailing-slash trim — this is inside a template literal, where a
    // regex like /\\/+$/ mis-escapes into broken emitted code (smoke:hooks catches it).
    let gwBase = cfgVal('SYNKRO_GATEWAY_URL') || 'https://api.synkro.sh';
    while (gwBase.endsWith('/')) gwBase = gwBase.slice(0, -1);
    const url = cloud
      ? gwBase + '/grade/submit'
      : 'http://127.0.0.1:' + PORT + '/api/scan/' + surface + (harness === 'cursor' ? '?harness=cursor' : '');
    const body = cloud
      ? JSON.stringify({ role: 'scan:' + surface, payload: JSON.stringify(envelope), content: '' })
      : JSON.stringify(envelope);
    const headers = { 'Content-Type': 'application/json', Authorization: 'Bearer ' + loadMcpJwt() };
    // Telemetry hooks (bash-followup, transcript-sync, session telemetry) IGNORE the
    // response and the server processes them DETACHED, so delivery RELIABILITY beats
    // latency: retry a few times with backoff. Right after a server restart the
    // event loop can be briefly busy / not-yet-listening and a single POST silently
    // drops the event (this was the followup flakiness). Double-delivery is safe here
    // — the server side is idempotent-ish (completion in-flight guard, dup evidence
    // is harmless). Blocking hooks make ONE attempt: their response IS the verdict
    // and a retry would double-grade. Normal case: first attempt wins, no delay.
    // prompt-submit is EXCLUDED from the retry: it runs on the interactive prompt
    // path under a 5s hook timeout, and 3×6s+backoff (19.5s) blew that budget and
    // fired "hook timed out after 5s" on every prompt whenever the server was slow.
    // Dropping an occasional prompt tick is harmless; stalling the user is not.
    const attempts = (opts.telemetry && surface !== 'prompt-submit') ? 3 : 1;
    let text = '';
    for (let i = 0; i < attempts; i++) {
      try {
        const resp = await fetch(url, { method: 'POST', headers, body, signal: AbortSignal.timeout(timeoutMs) });
        if (resp.ok) { text = (await resp.text()).trim(); break; }
      } catch (e) { /* connection refused / timeout → retry below */ }
      if (i < attempts - 1) await new Promise((r) => setTimeout(r, 500 * (i + 1)));
    }
    out(text || failOpen(harness));
  } catch {
    out(failOpen(harness));
  }
}
`;

function stubHook(surface: string, optsLiteral: string): string {
  return '#!/usr/bin/env bun\n' +
    "import { runStub } from './_synkro-stub-common.ts';\n" +
    'runStub(' + JSON.stringify(surface) + ', ' + optsLiteral + ');\n';
}

// edit/bash/cwe/cve/install ship the transcript tail too: the server reads the
// model (last assistant message) + user intent / consent history from it — the
// same context the old fat hooks extracted. Stub code stays 3 lines; this is
// just a flag telling the plumbing common to attach the tail.
export const STUB_EDIT_PRECHECK_TS = stubHook('edit-precheck', '{ needsFile: true, needsTranscript: true }');
export const STUB_CWE_PRECHECK_TS = stubHook('cwe-precheck', '{ needsFile: true, needsTranscript: true }');
export const STUB_CVE_PRECHECK_TS = stubHook('cve-precheck', '{ needsFile: true, needsTranscript: true }');
export const STUB_BASH_JUDGE_TS = stubHook('bash-judge', '{ needsTranscript: true }');
export const STUB_INSTALL_SCAN_TS = stubHook('install-scan', '{ needsTranscript: true }');
export const STUB_AGENT_JUDGE_TS = stubHook('agent-judge', '{ needsTranscript: true }');
// PreToolUse on mcp__* tools → MCP server governance (allow/blocklist + grade).
// Blocking, like the bash judge: the server's deny response stops the call.
export const STUB_MCP_GATE_TS = stubHook('mcp-gate', '{ needsTranscript: true }');
export const STUB_PLAN_JUDGE_TS = stubHook('plan-judge', '{ needsPlan: true }');
// NOT telemetry: the Stop hook also runs the task-adherence completion gate,
// which needs the full grading timeout and may return {decision:"block"} (passed
// through verbatim to CC). When no task is active the handler returns instantly,
// so stop stays fast in the common case.
export const STUB_STOP_SUMMARY_TS = stubHook('stop-summary', '{ needsTranscript: true, fullTranscript: true }');
export const STUB_SESSION_START_TS = stubHook('session-start', '{ telemetry: true }');
export const STUB_TRANSCRIPT_SYNC_TS = stubHook('transcript-sync', '{ needsTranscript: true, fullTranscript: true, telemetry: true }');
export const STUB_USER_PROMPT_SUBMIT_TS = stubHook('prompt-submit', '{ telemetry: true }');
export const STUB_BASH_FOLLOWUP_TS = stubHook('bash-followup', '{ telemetry: true }');

// PreToolUse hook on the activate_standard MCP tool. Fires IN the activating
// session (carrying its real session_id) just BEFORE the tool runs, and stamps
// the activation intent on the server so the task binds to THIS exact session —
// no most-recent-session race. Self-contained (doesn't need _synkro-stub-common);
// always allow ('{}'), never blocks the tool.
export const STUB_TASK_ACTIVATE_INTENT_TS = `#!/usr/bin/env bun
import { readFileSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';
const PORT = process.env.SYNKRO_MCP_PORT || '18931';
function mcpJwt(): string { try { return readFileSync(join(homedir(), '.synkro', '.mcp-jwt'), 'utf-8').trim(); } catch { return ''; } }
const chunks: Buffer[] = [];
for await (const chunk of process.stdin) chunks.push(Buffer.isBuffer(chunk) ? chunk : Buffer.from(chunk));
try {
  const payload = JSON.parse(Buffer.concat(chunks).toString('utf-8') || '{}');
  await fetch('http://127.0.0.1:' + PORT + '/api/local/task-activate-intent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + mcpJwt() },
    body: JSON.stringify({ session_id: payload.session_id, tool_input: payload.tool_input }),
    signal: AbortSignal.timeout(5000),
  });
} catch {}
process.stdout.write('{}\\n');
`;
export const STUB_CURSOR_BASH_JUDGE_TS = stubHook('bash-judge', "{ needsTranscript: true, harness: 'cursor' }");
export const STUB_CURSOR_EDIT_CAPTURE_TS = stubHook('edit-precheck', "{ needsFile: true, needsTranscript: true, harness: 'cursor' }");
export const STUB_CURSOR_AGENT_CAPTURE_TS = stubHook('agent-judge', "{ needsTranscript: true, harness: 'cursor' }");
